"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon";
import { Button } from "@/components";
import { cmsImageUnoptimized, resolveCmsImageSrc } from "@/lib/cms-image-url";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

/**
 * Five per page, on every page.
 *
 * The subscribe card is rendered inside the same grid on every page, so it
 * always occupies one slot. Pages after the first used to show six articles,
 * which made seven tiles in a three-column grid: two full rows and one orphan
 * sitting alone. Five plus the card fills two rows exactly.
 */
const FIRST_PAGE_ARTICLES = 5;
const OTHER_PAGE_ARTICLES = 5;

type BlogArticle = {
  id: string;
  title: string;
  slug: string;
  image: string | null;
};

function totalPagesForPool(length: number): number {
  if (length <= FIRST_PAGE_ARTICLES) return 1;
  return 1 + Math.ceil((length - FIRST_PAGE_ARTICLES) / OTHER_PAGE_ARTICLES);
}

function sliceForPage(pool: BlogArticle[], page: number): BlogArticle[] {
  if (page === 1) {
    return pool.slice(0, FIRST_PAGE_ARTICLES);
  }
  const start = FIRST_PAGE_ARTICLES + (page - 2) * OTHER_PAGE_ARTICLES;
  return pool.slice(start, start + OTHER_PAGE_ARTICLES);
}

/** Matches `app/search/page.tsx` desktop pagination window. */
function getPaginationItems(current: number, totalPages: number): Array<number | "…"> {
  if (totalPages <= 8) return Array.from({ length: totalPages }, (_, i) => i + 1);

  if (current <= 4) return [1, 2, 3, 4, 5, 6, "…", totalPages];

  if (current >= totalPages - 3) {
    return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "…", current - 1, current, current + 1, "…", totalPages];
}

/** Matches `app/search/page.tsx` mobile pagination (four page pills). */
function getMobilePaginationItems(current: number, totalPages: number): number[] {
  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (current <= 2) {
    return [1, 2, 3, 4];
  }
  if (current >= totalPages - 1) {
    return [totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [current - 1, current, current + 1, current + 2];
}

function SubscribeCard() {
  return (
    <aside className="min-w-0" aria-labelledby="blog-subscribe-heading">
      <div className="relative flex h-full min-h-[280px] w-full flex-col justify-center overflow-hidden rounded-2xl border border-neutral-10 bg-neutral-5 shadow-sm sm:min-h-[320px]">
        <Image
          src="/media/pages/contact/hero-image.webp"
          alt=""
          fill
          className="object-cover object-center"
          sizes="(max-width: 1024px) 100vw, 896px"
          aria-hidden
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-blue-100/90 via-blue-100/40 to-transparent" aria-hidden />
        <div className="relative z-[1] flex flex-col justify-center px-5 py-8 sm:px-8 sm:py-10">
          <div className="rounded-2xl border border-white/60 bg-white/90 px-5 py-6 shadow-sm backdrop-blur-sm sm:px-8 sm:py-7">
            <h2
              id="blog-subscribe-heading"
              className="text-center text-body-md font-bold leading-snug text-blue-80 sm:text-body-18"
            >
              Subscribe for new articles
            </h2>
            <p className="mt-2 text-center text-body-14 text-text-secondary sm:text-body-16-desktop">
              Get notified when we publish cannabis news and guides.
            </p>
            <form
              className="mt-5 flex w-full flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-3"
              onSubmit={(e) => e.preventDefault()}
            >
              <label htmlFor="blog-subscribe-email" className="sr-only">
                Email address
              </label>
              <input
                id="blog-subscribe-email"
                type="email"
                name="email"
                placeholder="Email address"
                autoComplete="email"
                className="w-full flex-1 rounded-xl border border-neutral-10 bg-constant-white py-2 pl-4 pr-4 text-body-16-desktop text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-blue-15"
              />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full rounded-xl px-6 sm:w-auto"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>
    </aside>
  );
}

function ArticleCard({ article }: { article: BlogArticle }) {
  const href = `/blog/${article.slug}`;
  return (
    <article className="flex flex-col gap-4">
      <Link
        href={href}
        className="relative block aspect-[16/10] w-full overflow-hidden rounded-2xl outline-none ring-0"
      >
        {article.image ? (
          <Image
            src={article.image}
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            unoptimized={cmsImageUnoptimized(article.image)}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-80" />
        )}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(23,22,90,0.45)_0%,rgba(52,59,145,0.15)_100%)]" />
      </Link>
      <div className="flex items-center justify-between gap-3">
        <Link href={href} className="group min-w-0 flex-1">
          <h3 className="line-clamp-3 text-body-16 font-bold leading-snug text-text-primary group- sm:text-body-16-desktop">
            {article.title}
          </h3>
        </Link>
        <Button asChild variant="primary" size="sm" className="!px-2">
          <Link href={href} aria-label={`Read: ${article.title}`}>
            <ChevronRightIcon size={16} color="currentColor" />
          </Link>
        </Button>
      </div>
    </article>
  );
}

export default function BlogListingSection() {
  const [pool, setPool] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [moreCount, setMoreCount] = useState(0);
  const totalPages = useMemo(() => totalPagesForPool(pool.length), [pool.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/pages?kind=BLOG&pageSize=100`, { cache: "no-store" });
        if (!res.ok) {
          if (!cancelled) setPool([]);
          return;
        }
        const data = await res.json();
        if (!cancelled && data?.success && Array.isArray(data.pages)) {
          const sorted = [...data.pages].sort((a: any, b: any) => {
            const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
            const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
            return tb - ta;
          });
          const rows: BlogArticle[] = sorted.map((item: any, idx: number) => {
            return {
              id: item.id || `blog-${idx}`,
              title: item.title || "Untitled blog post",
              slug: item.slug || "",
              image: resolveCmsImageSrc(item.featuredImage, "") || null,
            };
          });
          setPool(rows.filter((r) => Boolean(r.slug)));
        }
      } catch {
        if (!cancelled) setPool([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
    setMoreCount(0);
  }, [pool.length]);

  useEffect(() => {
    setMoreCount(0);
  }, [page]);

  const pageArticles = useMemo(
    () => sliceForPage(pool, Math.min(page, totalPages)),
    [pool, page, totalPages]
  );

  const currentPage = Math.min(page, Math.max(totalPages, 1));

  const indexAfterCurrentPage = useMemo(() => {
    if (currentPage === 1) return FIRST_PAGE_ARTICLES;
    return FIRST_PAGE_ARTICLES + (currentPage - 1) * OTHER_PAGE_ARTICLES;
  }, [currentPage]);

  const maxMoreCount = useMemo(() => {
    const remaining = Math.max(0, pool.length - indexAfterCurrentPage);
    return Math.ceil(remaining / 3);
  }, [pool.length, indexAfterCurrentPage]);

  const showMoreArticles = useMemo(() => {
    if (moreCount <= 0) return [];
    const n = moreCount * 3;
    return pool.slice(indexAfterCurrentPage, indexAfterCurrentPage + n);
  }, [moreCount, pool, indexAfterCurrentPage]);

  const displayedArticles = useMemo(
    () => [...pageArticles, ...showMoreArticles],
    [pageArticles, showMoreArticles]
  );

  const handleShowMore = useCallback(() => {
    setMoreCount((n) => Math.min(maxMoreCount, n + 1));
  }, [maxMoreCount]);

  if (loading) {
    return <section className="w-full py-8 text-center text-body-16 text-neutral-60">Loading blogs...</section>;
  }

  if (pool.length === 0) {
    return <section className="w-full py-8 text-center text-body-16 text-neutral-60">No blogs available yet.</section>;
  }

  return (
    <section className="w-full" aria-labelledby="blog-listing-heading">
      {/* Single grid: all visible posts, then subscribe CTA always last (full-width row). */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-10">
        {displayedArticles.map((article, idx) => (
          <ArticleCard
            key={`${article.id}-${currentPage}-${idx}-${moreCount}`}
            article={article}
          />
        ))}
        <SubscribeCard />
      </div>

      <div className="mt-10 sm:mt-8">
        {/* Mobile: same pattern as search page, Show more + pagination in one card */}
        <div className="lg:hidden rounded-2xl border border-neutral-10 bg-constant-white p-4 space-y-4">
          {totalPages > 1 ? (
            <nav className="flex items-center justify-center gap-2" aria-label="Pagination">
              <Button
                variant="icon"
                size="icon-sm"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg"
                aria-label="Previous page"
              >
                <ChevronLeftIcon size={18} color="currentColor" />
              </Button>

              {getMobilePaginationItems(currentPage, totalPages).map((pageNum) => {
                const active = pageNum === currentPage;
                return (
                  <Button
                    key={pageNum}
                    variant={active ? "primary" : "bordered"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="rounded-xl"
                  >
                    {String(pageNum)}
                  </Button>
                );
              })}

              <Button
                variant="icon"
                color="primary"
                size="icon-sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-xl"
                aria-label="Next page"
              >
                <ChevronRightIcon size={18} color="currentColor" />
              </Button>
            </nav>
          ) : null}

          {maxMoreCount > 0 ? (
            <Button
              variant="bordered"
              size="lg"
              onClick={handleShowMore}
              className="w-full rounded-xl px-4 py-3"
            >
              Read more
            </Button>
          ) : null}
        </div>

        {/* Desktop: matches search page */}
        <div className="hidden lg:block">
          <div className="mx-auto w-max rounded-2xl border border-neutral-10 bg-constant-white p-2">
            <div className="flex flex-col items-center gap-3">
              {totalPages > 1 ? (
                <nav className="flex items-center gap-2" aria-label="Pagination">
                  <Button
                    variant="icon"
                    size="icon-sm"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-xl"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon size={18} color="currentColor" />
                  </Button>

                  {getPaginationItems(currentPage, totalPages).map((it, idx) => {
                    if (it === "…") {
                      return (
                        <div
                          key={`dots-${idx}`}
                          className="inline-flex h-10 items-center justify-center px-3 text-body-16 font-bold text-text-tertiary"
                        >
                          …
                        </div>
                      );
                    }
                    const pageNum = it;
                    const active = pageNum === currentPage;
                    return (
                      <Button
                        key={pageNum}
                        variant={active ? "primary" : "bordered"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="h-10 min-w-10 rounded-xl border px-3 text-body-16 font-bold"
                      >
                        {String(pageNum)}
                      </Button>
                    );
                  })}

                  <Button
                    variant="icon"
                    size="icon-sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-xl"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon size={18} color="currentColor" />
                  </Button>
                </nav>
              ) : null}

              {maxMoreCount > 0 ? (
                <Button
                  variant="bordered"
                  size="md"
                  onClick={handleShowMore}
                  className="rounded-xl px-4"
                >
                  Read more
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
